import React, { useState, useEffect } from 'react';
import { Car, Plus, Search, Edit, Trash2, X, Settings, Building, Loader } from 'lucide-react';
import { empresasService, vehiculosService } from '../../../../services/api';
import useModal from '../../../../hooks/useModal';

const VehiculosTab = ({ vehiculos, setVehiculos }) => {
  const { notify, confirm, ModalRenderer } = useModal();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [formData, setFormData] = useState({
    placa: '',
    marca: '',
    modelo: '',
    anio: '',
    color: '',
    nombre: '',
    empresa_id: ''
  });

  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const res = await empresasService.getAll({ activo: true });
        const data = res?.data || res || [];
        setEmpresas(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error cargando empresas:', error);
      }
    };
    fetchEmpresas();
  }, []);

  const filteredVehiculos = vehiculos.filter(v => {
    const term = searchTerm.toLowerCase();
    const empNombre = v.empresa_nombre || '';
    return v.placa.toLowerCase().includes(term) ||
      v.marca.toLowerCase().includes(term) ||
      v.modelo.toLowerCase().includes(term) ||
      (v.nombre || '').toLowerCase().includes(term) ||
      empNombre.toLowerCase().includes(term);
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.placa || !formData.marca || !formData.modelo) {
      await notify('warning', 'Atención', 'Placa, marca y modelo son obligatorios');
      return;
    }
    
    const empresaSel = empresas.find(e => e.id.toString() === formData.empresa_id);
    
    setSaving(true);
    try {
      const res = await vehiculosService.create({
        placa: formData.placa,
        marca: formData.marca,
        modelo: formData.modelo,
        anio: formData.anio ? parseInt(formData.anio) : null,
        color: formData.color,
        nombre: formData.nombre || null,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
      });

      const created = res?.data || res;
      
      const newVehiculo = {
        ...created,
        empresa_nombre: empresaSel?.nombre || '',
      };
      
      setVehiculos(prev => [...prev, newVehiculo]);
      setShowModal(false);
      setFormData({ placa: '', marca: '', modelo: '', anio: '', color: '', nombre: '', empresa_id: '' });
    } catch (error) {
      const msg = error?.message || 'Error al guardar el vehículo en el servidor';
      await notify('error', 'Error', msg);
      console.error('Error creando vehículo:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Eliminar vehículo', '¿Está seguro de eliminar este vehículo?');
    if (!ok) return;
    try {
      await vehiculosService.delete(id);
      setVehiculos(prev => prev.filter(v => v.id !== id));
    } catch (error) {
      const msg = error?.message || 'Error al eliminar el vehículo';
      await notify('error', 'Error', msg);
      console.error('Error eliminando vehículo:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header y Acciones */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por placa, nombre, marca, modelo, empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
        >
          <Plus size={20} />
          Nuevo Vehículo
        </button>
      </div>

      {/* Lista de Vehículos */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredVehiculos.length === 0 ? (
          <div className="col-span-full py-12 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <Car size={48} className="mx-auto text-gray-400 mb-3" />
            <h3 className="text-lg font-medium text-gray-900">No hay vehículos encontrados</h3>
            <p className="text-gray-500 mt-1">Registra un nuevo vehículo para comenzar a generar órdenes.</p>
          </div>
        ) : (
          filteredVehiculos.map((vehiculo) => (
            <div key={vehiculo.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                    <Car size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">{vehiculo.placa}</h4>
                    {vehiculo.nombre && <span className="text-xs font-semibold text-blue-700 block">{vehiculo.nombre}</span>}
                    <span className="text-xs text-gray-500 font-medium">{vehiculo.marca} {vehiculo.modelo}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleDelete(vehiculo.id)} className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Año</p>
                  <p className="font-medium text-gray-800">{vehiculo.anio || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Color</p>
                  <p className="font-medium text-gray-800">{vehiculo.color || 'N/A'}</p>
                </div>
                {vehiculo.empresa_nombre && (
                  <div className="col-span-2">
                    <p className="text-gray-500 text-xs flex items-center gap-1"><Building size={12} /> Empresa</p>
                    <p className="font-medium text-gray-800">{vehiculo.empresa_nombre}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Registro</p>
                  <p className="font-medium text-gray-800">{new Date(vehiculo.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Nuevo Vehículo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Car size={20} className="text-blue-600" /> Nuevo Vehículo
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Placa / Patente *</label>
                <input
                  type="text"
                  name="placa"
                  value={formData.placa}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  placeholder="Ej: ABC-123"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Vehículo</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Camioneta de Reparto #1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Building size={14} /> Empresa *
                </label>
                <select
                  name="empresa_id"
                  value={formData.empresa_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  required
                >
                  <option value="">Seleccione una empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca *</label>
                  <input
                    type="text"
                    name="marca"
                    value={formData.marca}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Toyota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                  <input
                    type="text"
                    name="modelo"
                    value={formData.modelo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Corolla"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                  <input
                    type="number"
                    name="anio"
                    value={formData.anio}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 2021"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Blanco"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? <Loader size={16} className="animate-spin" /> : null}
                  {saving ? 'Guardando...' : 'Guardar Vehículo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {ModalRenderer}
    </div>
  );
};

export default VehiculosTab;
